import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import type { CurrentUser, SessionAccount } from '@xphoto/shared';
import { getActiveSlot, setActiveSlot, subscribeActiveSlot } from '../lib/activeSlot';
import { onUnauthorized } from '../lib/session-events';
import { recordAction, resetBuffers } from '../diagnostics/buffers';
import { clearGatewayAttempt } from './access-gateway';
import {
  exchangeAccessLink,
  fetchAccounts,
  fetchMe,
  login as apiLogin,
  logout as apiLogout,
  reshuffleRandomSeed as apiReshuffleRandomSeed,
} from './auth-api';

/**
 * Lit un lien d'accès communautaire `?access=<token>` de l'URL COURANTE et le RETIRE
 * immédiatement (via `history.replaceState`) — le jeton ne doit jamais rester dans la
 * barre d'adresse, l'historique ou les favoris après le premier rendu, quelle que soit
 * l'issue de l'échange. Renvoie le jeton (non vide) ou `null`. Tolérant à un
 * environnement sans `window`/`history` (SSR/tests) : ne lève jamais.
 *
 * On lit `window.location` directement (pas react-router) : l'interception a lieu au
 * tout début du boot d'authentification, avant que quoi que ce soit dépende du routeur.
 */
function consumeAccessToken(): string | null {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('access')) return null;
    const token = url.searchParams.get('access') ?? '';
    url.searchParams.delete('access');
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`,
    );
    return token.length > 0 ? token : null;
  } catch {
    return null;
  }
}

/**
 * Contexte d'authentification MULTI-LOGIN (T7a + T7c).
 *
 * Le navigateur empile plusieurs comptes (le « faisceau », côté serveur dans un
 * cookie chiffré) ; ce provider expose le compte ACTIF (slot courant), la liste
 * des comptes empilés, et les actions login/logout/switch/add.
 *
 * Discipline d'états (jamais de déconnexion sur une panne réseau) :
 *  - `me` renvoie 401 → `user = null` (slot sans session → écran de login) ;
 *  - `me`/`accounts` en échec réseau/5xx → `networkError`/`accountsError`
 *    (l'UI propose un « réessayer », PAS un retour au login) ;
 *  - `accounts` OK mais liste vide → déconnexion légitime.
 */

export interface AuthContextValue {
  /** Profil du compte actif (slot courant), ou `null` si aucun. */
  user: CurrentUser | null;
  /** Slot actif dans cet onglet, ou `null`. */
  activeSlot: number | null;
  /** Comptes actuellement empilés dans ce navigateur. */
  accounts: SessionAccount[];
  /** `me` du slot actif en cours. */
  loading: boolean;
  /** Chargement initial de la liste des comptes. */
  accountsLoading: boolean;
  /** Échec de `GET /accounts` (réseau/5xx). */
  accountsError: boolean;
  /** Échec de `GET /me` sur autre chose qu'un 401. */
  networkError: boolean;
  /** Empile un compte (vrai login) et le rend actif. `stayLoggedIn` = cookie persistant. */
  login: (email: string, password: string, stayLoggedIn: boolean) => Promise<void>;
  /** Déconnecte le compte du `slot` (ou TOUS si `all`). */
  logout: (slot: number, all?: boolean) => Promise<void>;
  /** Bascule le compte actif (sans re-login) et revient à l'accueil. */
  switchTo: (slot: number) => void;
  /** Va à l'écran de login pour empiler un nouveau compte. */
  addAccount: () => void;
  /** Recharge la liste des comptes. */
  refreshAccounts: () => Promise<void>;
  /** Recharge le profil du compte actif. */
  refreshMe: () => Promise<void>;
  /**
   * Régénère la graine de l'ordre aléatoire de la galerie côté serveur (persistée
   * par compte) et met à jour `user.randomSeed` du slot actif → les composants
   * re-rendent avec le nouvel ordre. Renvoie la nouvelle graine. Lève sur échec réseau.
   */
  reshuffleRandomSeed: () => Promise<string>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Projette un profil en entrée de compte empilé (mode forcé/tests). */
function accountFromUser(user: CurrentUser, slot: number): SessionAccount {
  return {
    slot,
    id: user.id,
    email: user.email,
    displayName: user.displayName,
    role: user.role,
    libraryId: user.libraryId,
  };
}

interface AuthProviderProps {
  children: React.ReactNode;
  /**
   * Forçage test : court-circuite les appels réseau (`accounts`/`me`).
   * `CurrentUser` → connecté (slot 0) ; `null` → anonyme ; `undefined` → mode réel.
   */
  initialUser?: CurrentUser | null;
}

export function AuthProvider({ children, initialUser }: AuthProviderProps): React.JSX.Element {
  const forced = initialUser !== undefined;
  const navigate = useNavigate();

  const [activeSlot, setActiveSlotState] = useState<number | null>(() =>
    forced ? (initialUser ? 0 : null) : getActiveSlot(),
  );
  const [user, setUser] = useState<CurrentUser | null>(() => initialUser ?? null);
  const [accounts, setAccounts] = useState<SessionAccount[]>(() =>
    forced && initialUser ? [accountFromUser(initialUser, 0)] : [],
  );
  const [loading, setLoading] = useState<boolean>(!forced);
  const [accountsLoading, setAccountsLoading] = useState<boolean>(!forced);
  const [accountsError, setAccountsError] = useState(false);
  const [networkError, setNetworkError] = useState(false);

  /**
   * Slot dont le profil vient d'être POSÉ par une voie qui le renvoyait déjà (audit A8) :
   * `POST /auth/login` et l'échange de lien d'accès rendent le `CurrentUser` complet.
   * Sans ce témoin, `setActiveSlot` notifiait le store, l'effet « profil » se ré-exécutait
   * et refaisait un `GET /auth/me` pour ré-obtenir ce qu'on venait de recevoir —
   * séquence `login` → `accounts` → `me`, dont le dernier appel était superflu.
   *
   * Le témoin est consommé (remis à `null`) à CHAQUE passage de l'effet « profil », pas
   * seulement quand il sert : un témoin non consommé — cas où l'effet ne se déclenche pas
   * parce que le slot était déjà l'actif, donc où aucune requête n'était de toute façon
   * émise — est effacé par le premier passage suivant, qui est précisément celui qui
   * aurait pu le mal-utiliser. Aucune resynchronisation (401, bascule de compte,
   * `refreshMe`) n'est donc sautée.
   */
  const freshProfileSlotRef = useRef<number | null>(null);

  const refreshAccounts = useCallback(async () => {
    setAccountsLoading(true);
    setAccountsError(false);
    try {
      const list = await fetchAccounts();
      setAccounts(list);
      // Défaut : slot actif absent des comptes → plus petit slot connecté (ne
      // CLOBBER jamais un slot valide non-minimal).
      const slots = list.map((a) => a.slot).sort((a, b) => a - b);
      const cur = getActiveSlot();
      if (cur === null || !slots.includes(cur)) {
        setActiveSlot(slots.length > 0 ? slots[0]! : null);
      }
    } catch {
      setAccountsError(true);
    } finally {
      setAccountsLoading(false);
    }
  }, []);

  // Session ouverte, PAR QUELQUE VOIE QUE CE SOIT (échange de lien, login, session
  // déjà valide au boot) → on purge l'anti-boucle de la passerelle (ADR-0008) : le
  // prochain visiteur sans session, dans cet onglet, aura de nouveau droit à son
  // aller-retour. Sans ça, une déconnexion volontaire condamnerait l'onglet à
  // l'écran de connexion jusqu'à sa fermeture.
  useEffect(() => {
    if (user !== null) clearGatewayAttempt();
  }, [user]);

  // Bootstrap + abonnement au store de slot (mode réel uniquement).
  useEffect(() => {
    if (forced) return;
    const unsubscribe = subscribeActiveSlot(() => {
      const slot = getActiveSlot();
      setActiveSlotState(slot);
      // Un slot vient d'être désigné → profil « en cours de résolution ». Sans ce
      // `loading` synchrone, un render intercalé (avant l'effet `me`) verrait
      // user=null + loading=false et redirigerait à tort vers /login.
      if (slot !== null) setLoading(true);
    });

    // Lien d'accès communautaire `?access=<token>` (Incrément A2). On retire TOUJOURS
    // le param de l'URL immédiatement (jamais laissé après le 1er rendu), puis on tente
    // l'échange. Succès → ouvre la session (comme `login`) ; échec (401 lien invalide ou
    // réseau) → repli SILENCIEUX sur le flux normal (→ login). `loading`/`accountsLoading`
    // restent à `true` pendant l'échange (état initial, mode réel) → PAS de flash de login.
    const accessToken = consumeAccessToken();
    if (accessToken !== null) {
      exchangeAccessLink(accessToken)
        .then((data) => {
          recordAction('auth.linkExchange', { slot: data.slot });
          // Profil déjà en main → l'effet « profil » n'a pas à le redemander (audit A8).
          freshProfileSlotRef.current = data.slot;
          setActiveSlot(data.slot);
          setUser(data.user);
          return refreshAccounts();
        })
        .catch(() => refreshAccounts());
    } else {
      void refreshAccounts();
    }

    return unsubscribe;
  }, [forced, refreshAccounts]);

  // Profil du slot actif (mode réel uniquement).
  useEffect(() => {
    if (forced) return;
    if (activeSlot === null) {
      freshProfileSlotRef.current = null;
      setUser(null);
      setLoading(false);
      setNetworkError(false);
      return;
    }
    if (freshProfileSlotRef.current === activeSlot) {
      freshProfileSlotRef.current = null;
      setLoading(false);
      setNetworkError(false);
      return;
    }
    freshProfileSlotRef.current = null;
    const controller = new AbortController();
    setLoading(true);
    setNetworkError(false);
    fetchMe(activeSlot, controller.signal)
      .then((me) => {
        setUser(me); // `null` (401) = slot sans session → géré comme non authentifié.
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // 5xx / réseau : on NE déconnecte PAS, on signale.
        setNetworkError(true);
        setLoading(false);
      });
    return () => controller.abort();
  }, [forced, activeSlot]);

  /** Un 401 applicatif est déjà en cours de traitement (anti-rafale). */
  const handlingUnauthorizedRef = useRef(false);

  /**
   * 401 EN COURS DE NAVIGATION (audit F3).
   *
   * `apiFetch` signale tout 401 reçu HORS `/api/auth/*` (les routes d'auth répondent
   * 401 comme réponse métier : identifiants faux, slot vide, lien invalide, mot de
   * passe actuel erroné — les inclure boucherait sur l'écran de connexion). Cas réel :
   * un porteur de lien d'accès RÉVOQUÉ pendant qu'il navigue (v1.11.0). Avant ce
   * correctif, toutes ses actions échouaient en « Erreur » générique et il restait
   * bloqué jusqu'à un F5 manuel.
   *
   * MULTI-LOGIN — tranché : un 401 n'invalide QUE le slot visé, JAMAIS le faisceau.
   *  - Le slot de la requête voyage avec l'événement ; s'il n'est plus l'actif (requête
   *    partie avant une bascule de compte), on IGNORE — sinon une réponse retardataire
   *    déconnecterait le compte vers lequel l'utilisateur vient de basculer.
   *  - Sinon on vide le profil actif PUIS on resynchronise le faisceau auprès du
   *    serveur (`refreshAccounts`), seule source de vérité de ce qui reste ouvert :
   *    s'il reste un compte empilé, `refreshAccounts` sélectionne le plus petit slot
   *    restant → l'utilisateur ATTERRIT SUR CE COMPTE au lieu d'être jeté au login ;
   *    s'il n'en reste aucun, le slot passe à `null` → `PrivateRoute` bascule seul
   *    sur `/login`. Aucun compte sain n'est déconnecté par ricochet.
   *  - Garde anti-rafale : une page de galerie émet des dizaines de requêtes ; un
   *    seul traitement à la fois (les 401 suivants du même incident sont absorbés).
   */
  useEffect(() => {
    if (forced) return;
    return onUnauthorized(({ slot }) => {
      if (slot !== getActiveSlot()) return; // slot obsolète : ne concerne plus cet onglet
      if (handlingUnauthorizedRef.current) return; // rafale : un seul traitement
      handlingUnauthorizedRef.current = true;
      setUser(null);
      setLoading(false);
      void refreshAccounts().finally(() => {
        handlingUnauthorizedRef.current = false;
      });
    });
  }, [forced, refreshAccounts]);

  const login = useCallback(
    async (email: string, password: string, stayLoggedIn: boolean) => {
      const data = await apiLogin(email, password, stayLoggedIn);
      recordAction('auth.login', { slot: data.slot });
      // `apiLogin` renvoie DÉJÀ le profil : on le mémorise pour que l'effet « profil »
      // saute son `GET /auth/me` (audit A8). Posé AVANT `setActiveSlot`, qui notifie le
      // store de slot de façon synchrone.
      freshProfileSlotRef.current = data.slot;
      setActiveSlot(data.slot);
      setUser(data.user);
      await refreshAccounts();
    },
    [refreshAccounts],
  );

  const logout = useCallback(
    async (slot: number, all = false) => {
      recordAction('auth.logout', { slot, all: all ? 1 : 0 });
      await apiLogout(slot, all);
      // Déconnexion totale : purge les anneaux de diagnostic (aucune activité d'un
      // compte ne survit dans le rapport d'un autre).
      if (all) resetBuffers();
      await refreshAccounts(); // re-sélectionne le plus petit slot restant (ou null).
    },
    [refreshAccounts],
  );

  const switchTo = useCallback(
    (slot: number) => {
      recordAction('auth.switch', { slot });
      setActiveSlot(slot);
      navigate('/');
    },
    [navigate],
  );

  const addAccount = useCallback(() => {
    recordAction('auth.addAccount');
    navigate('/login');
  }, [navigate]);

  const refreshMe = useCallback(async () => {
    const slot = getActiveSlot();
    if (slot === null) {
      setUser(null);
      return;
    }
    try {
      setUser(await fetchMe(slot));
      setNetworkError(false);
    } catch {
      setNetworkError(true);
    }
  }, []);

  const reshuffleRandomSeed = useCallback(async (): Promise<string> => {
    // Cible le slot ACTIF (comme `me`/`logout`). `activeSlot` est la source de vérité
    // du provider ; repli sur le store (`getActiveSlot`) puis 0 pour ne jamais planter.
    const slot = activeSlot ?? getActiveSlot() ?? 0;
    const seed = await apiReshuffleRandomSeed(slot);
    recordAction('gallery.reshuffleSeed');
    // Met à jour la graine du profil actif → l'ordre aléatoire par défaut la reflète.
    setUser((prev) => (prev ? { ...prev, randomSeed: seed } : prev));
    return seed;
  }, [activeSlot]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      activeSlot,
      accounts,
      loading,
      accountsLoading,
      accountsError,
      networkError,
      login,
      logout,
      switchTo,
      addAccount,
      refreshAccounts,
      refreshMe,
      reshuffleRandomSeed,
    }),
    [
      user,
      activeSlot,
      accounts,
      loading,
      accountsLoading,
      accountsError,
      networkError,
      login,
      logout,
      switchTo,
      addAccount,
      refreshAccounts,
      refreshMe,
      reshuffleRandomSeed,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Accès au contexte d'authentification. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit être utilisé dans un <AuthProvider>');
  return ctx;
}
